import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import { MessageSchema } from './primitives.js';
import { TaskPrioritySchema, TaskStatusSchema } from './tasks.js';
const c = initContract();
/**
 * Project Tasks (PART 13, PRJ-02) — the ONE task engine, extended.
 *
 * BR-PRJ-02-01, verbatim: "A project task must be a row in the shared task
 * table, related to a project. There must be no separate project-task
 * table." Creating one here inserts a `tasks` row (relatedKind 'project')
 * plus its five extra fields — so it appears on its assignee's My-Tasks and
 * Follow-ups panels beside their sales calls, with zero extra machinery.
 *
 * The two-status model (§5b): the PROJECT status (configurable per project,
 * BR-PRJ-02-05) is what this screen moves; the SHARED status is mirrored
 * automatically — a done-marked status ⇒ 'completed', anything else ⇒
 * 'pending' (BR-PRJ-02-04). Moving to done stamps the completion time;
 * moving away clears it (§7).
 *
 * Derived, never stored:
 *   - actualHours = Σ APPROVED timesheet hours for the task (BR-PRJ-02-02/03)
 *   - blocked     = any dependency's status is not done (BR-PRJ-02-06);
 *                   completing a blocker unblocks downstream at once
 *                   (BR-PRJ-02-09) because nothing is cached.
 *
 * BR-PRJ-02-08: moving a blocked task to done is ALLOWED and answers the
 * warning sentence naming the open count. BR-PRJ-02-10: a dependency cycle
 * is refused (service + DB trigger). BR-PRJ-02-15: the subtask tick is a
 * real write gated on may-change-that-task; un-ticking restores the
 * PREVIOUS status (the un-tick defect, fixed).
 */
// ── The fixed sentences ─────────────────────────────────────────────────────
export const PROJECT_TASK_MESSAGES = {
    /**
     * BR-PRJ-02-08 / ERR-PRJ-02-04 — the exact warning, with its count, and the
     * move goes THROUGH. A dependency is advice about order, not a lock.
     */
    blockedDoneWarning: (open) => `Warning: still blocked by ${open} incomplete task(s) — marked Done anyway`,
    /** BR-PRJ-02-10. */
    dependencyCycle: 'That dependency would form a cycle and is refused.',
    dependencySelf: 'A task cannot depend on itself.',
    dependencyExists: 'That dependency already exists.',
    /** Q-PRJ-02-5 (refused — and unrepresentable at the DB). */
    parentOtherProject: 'A subtask must belong to the same project as its parent.',
    /** BR-PRJ-02-13. */
    hasEntries: 'This task has timesheet entries and cannot be deleted.',
    notProjectTask: 'This task is not a project task.',
    statusUnknown: 'That status does not exist on this project.',
    /** The PRJ-02 §5 stat-tile footnote, drawn under Actual hours. */
    rolledUp: 'Rolled up from timesheet entries — not manually edited.',
    notYourTask: 'This task is assigned to somebody else. Ask your administrator.',
    /**
     * BR-ADM-05-08 — *"A suspended person must not be offered in any picker …
     * not as a task owner."* The picker no longer offers them; this is the
     * sentence for every other route, in the same shape as
     * `PROJECT_MESSAGES.employeeTerminated` which says the same thing on the
     * employee axis. The spec prescribes no wording for the task-owner case —
     * ADM-05 §14 has no row for it — so this is the build's own sentence and is
     * NOT cited as an ERR- identifier.
     */
    assigneeNotAssignable: (name) => `${name}'s account is switched off and cannot be given a task.`,
};
/**
 * D46 — PRJ's unassignable-assignee refusal, structured. `name` is a display
 * value. Sentence parity pinned by `refusal-census.spec.ts`.
 */
export const PROJECT_TASK_REFUSALS = {
    assigneeNotAssignable: (name) => ({
        message: PROJECT_TASK_MESSAGES.assigneeNotAssignable(name),
        key: 'prj.errAssigneeNotAssignable',
        params: { name },
    }),
};
/** The default five (§5b/§7), seeded per project, then configurable. */
export const DEFAULT_TASK_STATUSES = [
    { name: 'To Do', color: 'gray', isDone: false },
    { name: 'In Progress', color: 'blue', isDone: false },
    { name: 'In Review', color: 'violet', isDone: false },
    { name: 'Done', color: 'green', isDone: true },
    { name: 'Blocked', color: 'red', isDone: false },
];
// ── Shapes ──────────────────────────────────────────────────────────────────
export const ProjectTaskStatusSchema = z
    .object({
    id: z.uuid(),
    position: z.number().int().min(1),
    name: z.string(),
    color: z.string(),
    /** BR-PRJ-02-04's mirror axis. */
    isDone: z.boolean(),
})
    .strict();
export const DependencySchema = z
    .object({
    taskId: z.uuid(),
    title: z.string(),
    /** Green dot when done, red when still open (§13's detail panel). */
    done: z.boolean(),
})
    .strict();
export const ProjectTaskSchema = z
    .object({
    /** The SHARED task's id — one record, one id (BR-PRJ-02-01). */
    taskId: z.uuid(),
    projectId: z.uuid(),
    title: z.string(),
    assigneeId: z.uuid(),
    assigneeName: z.string().nullable(),
    dueAt: z.string(),
    priority: TaskPrioritySchema,
    /** The project status axis (§5b). */
    statusId: z.uuid(),
    statusName: z.string(),
    statusIsDone: z.boolean(),
    /** The mirrored shared status the rest of the product sees. */
    sharedStatus: TaskStatusSchema,
    estimatedHours: z.string().nullable(),
    /** DERIVED: Σ approved timesheet hours (BR-PRJ-02-02). */
    actualHours: z.string(),
    parentTaskId: z.uuid().nullable(),
    /** DERIVED every read: any open dependency (BR-PRJ-02-06). */
    blocked: z.boolean(),
    blockedBy: DependencySchema.array(),
    subtaskCount: z.number().int().min(0),
    subtaskDoneCount: z.number().int().min(0),
    completedAt: z.string().nullable(),
})
    .strict();
/** The PRJ-02 §4 summary header — counts cover EVERY task (BR-PRJ-02-11). */
export const TaskBoardHeaderSchema = z
    .object({
    totalCount: z.number().int().min(0),
    doneCount: z.number().int().min(0),
    completionPct: z.number().int().min(0).max(100),
    estimatedHours: z.string(),
    actualHours: z.string(),
    perStatus: z
        .object({ statusId: z.uuid(), name: z.string(), color: z.string(), count: z.number().int().min(0) })
        .strict()
        .array(),
})
    .strict();
export const CreateProjectTaskRequestSchema = z
    .object({
    title: z.string().trim().min(1).max(200),
    /** Omitted = the caller (the engine's own default). */
    assigneeId: z.uuid().optional(),
    dueAt: z.iso.datetime({ offset: true }),
    priority: TaskPrioritySchema.optional(),
    statusId: z.uuid().optional(),
    estimatedHours: z
        .string()
        .regex(/^\d{1,4}(\.\d{1,2})?$/, 'Hours are decimal strings with at most 2 decimals')
        .optional(),
    parentTaskId: z.uuid().optional(),
})
    .strict();
export const UpdateProjectTaskRequestSchema = z
    .object({
    title: z.string().trim().min(1).max(200).optional(),
    assigneeId: z.uuid().optional(),
    dueAt: z.iso.datetime({ offset: true }).optional(),
    priority: TaskPrioritySchema.optional(),
    /** BR-PRJ-02-14: estimates are editable. Null clears one. */
    estimatedHours: z
        .string()
        .regex(/^\d{1,4}(\.\d{1,2})?$/, 'Hours are decimal strings with at most 2 decimals')
        .nullable()
        .optional(),
})
    .strict();
const projectParams = z.object({ projectId: z.uuid() }).strict();
const taskParams = z.object({ taskId: z.uuid() }).strict();
const boardResponse = z
    .object({
    statuses: ProjectTaskStatusSchema.array(),
    tasks: ProjectTaskSchema.array(),
    header: TaskBoardHeaderSchema,
})
    .strict();
const taskWithWarning = z
    .object({
    task: ProjectTaskSchema,
    /** BR-PRJ-02-08's sentence, when a still-blocked task was marked done. */
    warning: z.string().nullable(),
})
    .strict();
export const projectTasksContract = c.router({
    board: {
        method: 'GET',
        path: '/api/projects/:projectId/tasks',
        pathParams: projectParams,
        responses: { 200: boardResponse, 401: MessageSchema, 403: MessageSchema, 404: MessageSchema },
        summary: "PRJ-02: the project's statuses + every task with DERIVED actual hours and blocked-ness. Header counts include subtasks; board/list draw top-level only (BR-PRJ-02-11, client-side by parentTaskId).",
    },
    create: {
        method: 'POST',
        path: '/api/projects/:projectId/tasks',
        pathParams: projectParams,
        body: CreateProjectTaskRequestSchema,
        responses: { 201: ProjectTaskSchema, 400: MessageSchema, 401: MessageSchema, 403: MessageSchema, 404: MessageSchema, 409: MessageSchema },
        summary: "BR-PRJ-02-01/-14: ONE insert into the shared task table (relatedKind 'project') + the five extra fields. A parent makes it a subtask — same project only (Q-PRJ-02-5).",
    },
    update: {
        method: 'PATCH',
        path: '/api/projects/tasks/:taskId',
        pathParams: taskParams,
        body: UpdateProjectTaskRequestSchema,
        // 409 is new here and every other mutating route in this file already had
        // it: reassigning a task to a SWITCHED-OFF account is refused
        // (BR-ADM-05-08), and a route that can refuse must say so.
        responses: { 200: ProjectTaskSchema, 400: MessageSchema, 401: MessageSchema, 403: MessageSchema, 404: MessageSchema, 409: MessageSchema },
        summary: 'BR-PRJ-02-14: title, assignee, due, priority and the ESTIMATE are editable. BR-ADM-05-08: the assignee must be an account that may still be offered.',
    },
    setStatus: {
        method: 'POST',
        path: '/api/projects/tasks/:taskId/status',
        pathParams: taskParams,
        body: z.object({ statusId: z.uuid() }).strict(),
        responses: { 200: taskWithWarning, 401: MessageSchema, 403: MessageSchema, 404: MessageSchema, 409: MessageSchema },
        summary: 'Move a task (drag or detail panel): mirrors the shared status (BR-PRJ-02-04), stamps/clears completion, and moving a BLOCKED task to done is allowed WITH the warning sentence (BR-PRJ-02-08). Assignee or projects.edit.',
    },
    tick: {
        method: 'POST',
        path: '/api/projects/tasks/:taskId/tick',
        pathParams: taskParams,
        body: z.object({}).strict().optional(),
        responses: { 200: taskWithWarning, 401: MessageSchema, 403: MessageSchema, 404: MessageSchema, 409: MessageSchema },
        summary: 'BR-PRJ-02-15: the subtask tick — a real write to done, remembering the previous status. Completing a blocker releases everything downstream at once (BR-PRJ-02-09).',
    },
    untick: {
        method: 'POST',
        path: '/api/projects/tasks/:taskId/untick',
        pathParams: taskParams,
        body: z.object({}).strict().optional(),
        responses: { 200: taskWithWarning, 401: MessageSchema, 403: MessageSchema, 404: MessageSchema, 409: MessageSchema },
        summary: 'Un-tick: RESTORES the status the subtask held before its tick (the un-tick defect, fixed).',
    },
    addDependency: {
        method: 'POST',
        path: '/api/projects/tasks/:taskId/dependencies',
        pathParams: taskParams,
        body: z.object({ blockedByTaskId: z.uuid() }).strict(),
        responses: { 200: ProjectTaskSchema, 400: MessageSchema, 401: MessageSchema, 403: MessageSchema, 404: MessageSchema, 409: MessageSchema },
        summary: 'BR-PRJ-02-14: dependencies are editable — and the cycle check is finally CALLED (BR-PRJ-02-10; the DB trigger is the floor). Same project only.',
    },
    removeDependency: {
        method: 'DELETE',
        path: '/api/projects/tasks/:taskId/dependencies/:blockedByTaskId',
        pathParams: taskParams.extend({ blockedByTaskId: z.uuid() }).strict(),
        responses: { 200: ProjectTaskSchema, 401: MessageSchema, 403: MessageSchema, 404: MessageSchema },
        summary: 'Remove a dependency edge. Blocked-ness is re-derived immediately.',
    },
    updateStatusConfig: {
        method: 'PATCH',
        path: '/api/projects/task-statuses/:statusId',
        pathParams: z.object({ statusId: z.uuid() }).strict(),
        body: z
            .object({
            name: z.string().trim().min(1).max(40).optional(),
            color: z.string().trim().max(20).optional(),
            position: z.number().int().min(1).optional(),
        })
            .strict(),
        responses: { 200: ProjectTaskStatusSchema.array(), 400: MessageSchema, 401: MessageSchema, 403: MessageSchema, 404: MessageSchema, 409: MessageSchema },
        summary: "BR-PRJ-02-05: rename / recolour / reorder a project's statuses, exactly as CRM stages.",
    },
    addStatusConfig: {
        method: 'POST',
        path: '/api/projects/:projectId/task-statuses',
        pathParams: projectParams,
        body: z
            .object({
            name: z.string().trim().min(1).max(40),
            color: z.string().trim().max(20).optional(),
        })
            .strict(),
        responses: { 201: ProjectTaskStatusSchema.array(), 400: MessageSchema, 401: MessageSchema, 403: MessageSchema, 404: MessageSchema, 409: MessageSchema },
        summary: 'BR-PRJ-02-05: a project can add its own status (appended before the done column).',
    },
}, { strictStatusCodes: true });
//# sourceMappingURL=project-tasks.js.map